import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Checkbox,
  ListItemText,
  FormHelperText,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  styled
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  CheckBox as CheckboxIcon, 
  CheckBoxOutlineBlank as CheckboxBlankIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Supplier, UtilityType } from '../../../types/utility.types';
import * as supplierApi from '../../../api/suppliers';
import { SelectChangeEvent } from '@mui/material/Select';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const initialFormData: Omit<Supplier, '_id'> = {
  name: '',
  serviceTypes: [],
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  paymentTerms: {
    commissionRate: 0,
    paymentDays: 30,
    paymentMethod: 'BANK_TRANSFER',
    paymentFrequency: 'UPFRONT',
  },
  notes: '',
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date(),
};

const UTILITY_TYPES: UtilityType[] = [
  'Gas',
  'Electricity',
  'Water',
  'Telecoms',
  'Waste',
  'Merchant Services',
  'PPA Export Contract',
  'EV',
  'Renewable',
  'Insurance',
];

// Styled component for clickable row
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Dark theme TextField styling
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
    },
    '& input': {
      color: 'text.primary',
    },
    '& textarea': {
      color: 'text.primary',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: 'primary.main',
    },
  },
};

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('name');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedSuppliers = await supplierApi.fetchSuppliers();
      setSuppliers(fetchedSuppliers);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch suppliers';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        serviceTypes: supplier.serviceTypes,
        contactName: supplier.contactName || '',
        contactEmail: supplier.contactEmail || '',
        contactPhone: supplier.contactPhone || '',
        website: supplier.website || '',
        paymentTerms: supplier.paymentTerms || initialFormData.paymentTerms,
        notes: supplier.notes || '',
        isActive: supplier.isActive,
        createdAt: supplier.createdAt,
        updatedAt: supplier.updatedAt,
      });
    } else {
      setEditingSupplier(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (formData.serviceTypes.length === 0) {
      enqueueSnackbar('Please select at least one service type', { variant: 'error' });
      return;
    }
    
    if (!formData.contactName || !formData.contactName.trim()) {
      enqueueSnackbar('Contact name is required', { variant: 'error' });
      return;
    }
    
    try {
      if (editingSupplier) {
        await supplierApi.updateSupplier(editingSupplier._id, formData);
        enqueueSnackbar('Supplier updated successfully', { variant: 'success' });
      } else {
        await supplierApi.createSupplier(formData);
        enqueueSnackbar('Supplier created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchSuppliers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save supplier';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDelete = async (supplier: Supplier) => {
    if (window.confirm(`Are you sure you want to delete ${supplier.name}?`)) {
      try {
        await supplierApi.deleteSupplier(supplier._id);
        enqueueSnackbar('Supplier deleted successfully', { variant: 'success' });
        
        // Close the modal and reset state after successful deletion
        setOpenDialog(false);
        setEditingSupplier(null);
        setFormData(initialFormData);
        
        fetchSuppliers();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete supplier';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNestedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!name) return;

    const [parent, child] = name.split('.');
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof typeof prev] as Record<string, unknown> || {}),
        [child]: value,
      },
    }));
  };

  const handleNestedSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (!name) return;

    const handleNestedChange = (parent: string, child: string, value: any) => {
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as Record<string, unknown> || {}),
          [child]: value,
        },
      }));
    };

    const [parent, child] = name.split('.');
    handleNestedChange(parent, child, value);
  };

  // Filtered and sorted suppliers
  const filteredSuppliers = useMemo(() => {
    let filtered = suppliers.filter(supplier => {
      const matchesSearch = !searchTerm || 
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contactPhone?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && supplier.isActive) ||
        (statusFilter === 'inactive' && !supplier.isActive);
      
      const matchesServiceType = serviceTypeFilter === 'all' || 
        supplier.serviceTypes.includes(serviceTypeFilter as UtilityType);
      
      return matchesSearch && matchesStatus && matchesServiceType;
    });

    // Sort suppliers
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (orderBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'contactName':
          aValue = a.contactName || '';
          bValue = b.contactName || '';
          break;
        case 'contactPhone':
          aValue = a.contactPhone || '';
          bValue = b.contactPhone || '';
          break;
        case 'isActive':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    return filtered;
  }, [suppliers, searchTerm, statusFilter, serviceTypeFilter, order, orderBy]);

  const paginatedSuppliers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredSuppliers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredSuppliers, page, rowsPerPage]);

  const uniqueServiceTypes = useMemo(() => {
    const types = new Set<UtilityType>();
    suppliers.forEach(supplier => {
      supplier.serviceTypes.forEach(type => types.add(type));
    });
    return Array.from(types);
  }, [suppliers]);

  if (loading && !suppliers.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSort = (property: keyof Supplier) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleViewSupplier = (supplier: Supplier) => {
    handleOpenDialog(supplier);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Suppliers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Supplier
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search suppliers..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            label="Status"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Service Type</InputLabel>
          <Select
            value={serviceTypeFilter}
            onChange={(e) => { setServiceTypeFilter(e.target.value); setPage(0); }}
            label="Service Type"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All Types</MenuItem>
            {uniqueServiceTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {filteredSuppliers.length === 0 && !error && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            {searchTerm || statusFilter !== 'all' || serviceTypeFilter !== 'all'
              ? 'No suppliers match your search criteria'
              : 'No suppliers found'
            }
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm || statusFilter !== 'all' || serviceTypeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Click the "Add Supplier" button above to add your first supplier.'
            }
          </Typography>
        </Paper>
      )}

      {filteredSuppliers.length > 0 && (
        <>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        <Typography fontWeight="bold">Name</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'serviceTypes'}
                        direction={orderBy === 'serviceTypes' ? order : 'asc'}
                        onClick={() => handleSort('serviceTypes')}
                      >
                        <Typography fontWeight="bold">Service Types</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><Typography fontWeight="bold">Contact Info</Typography></TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'commissionRate'}
                        direction={orderBy === 'commissionRate' ? order : 'asc'}
                        onClick={() => handleSort('paymentTerms')}
                      >
                        <Typography fontWeight="bold">Commission Rate</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><Typography fontWeight="bold">Payment Terms</Typography></TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'isActive'}
                        direction={orderBy === 'isActive' ? order : 'asc'}
                        onClick={() => handleSort('isActive')}
                      >
                        <Typography fontWeight="bold">Status</Typography>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSuppliers.map((supplier) => (
                    <StyledTableRow 
                      key={supplier._id}
                      onClick={() => handleViewSupplier(supplier)}
                      hover
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle2">{supplier.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {supplier.serviceTypes.slice(0, 3).map((type) => (
                            <Chip
                              key={type}
                              label={type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                          {supplier.serviceTypes.length > 3 && (
                            <Chip
                              label={`+${supplier.serviceTypes.length - 3} more`}
                              size="small"
                              color="default"
                              variant="outlined"
                              onClick={() => {
                                enqueueSnackbar(
                                  `All services: ${supplier.serviceTypes.join(', ')}`,
                                  { variant: 'info' }
                                );
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{supplier.contactName}</Typography>
                        <Typography variant="body2" color="text.secondary">{supplier.contactPhone}</Typography>
                        {supplier.contactEmail && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {supplier.contactEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {supplier.paymentTerms?.commissionRate}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {supplier.paymentTerms?.paymentFrequency?.toLowerCase()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {supplier.paymentTerms?.paymentDays} days
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={supplier.isActive ? 'Active' : 'Inactive'}
                          color={supplier.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ minWidth: 80 }}
                        />
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={filteredSuppliers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '0.75rem',
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 2,
            color: 'white'
          }}>
            {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Fields marked with an asterisk (*) are required
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Services Offered *</Typography>
                <FormControl fullWidth required>
                  <InputLabel id="service-types-label">Service Types *</InputLabel>
                  <Select
                    labelId="service-types-label"
                    id="service-types"
                    multiple
                    value={formData.serviceTypes}
                    onChange={(e) => {
                      const value = e.target.value as UtilityType[];
                      setFormData({ ...formData, serviceTypes: value });
                    }}
                    label="Service Types"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '& .MuiSelect-select': {
                        color: 'text.primary',
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          width: 250,
                          backgroundColor: 'rgba(30, 30, 30, 0.95)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        },
                      },
                    }}
                  >
                    {UTILITY_TYPES.map((type) => (
                      <MenuItem 
                        key={type} 
                        value={type}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                          },
                        }}
                      >
                        <Checkbox checked={formData.serviceTypes.includes(type)} />
                        <ListItemText primary={type} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select all utility services that this supplier can provide
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Contact Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Name"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      helperText="Primary contact person for this supplier"
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      required
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      type="email"
                      helperText="Optional email address for this contact"
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Payment Terms</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Commission Rate (%)"
                      name="paymentTerms.commissionRate"
                      value={formData.paymentTerms?.commissionRate}
                      onChange={handleNestedChange}
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Payment Days"
                      name="paymentTerms.paymentDays"
                      value={formData.paymentTerms?.paymentDays}
                      onChange={handleNestedChange}
                      type="number"
                      inputProps={{ min: 0 }}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        label="Payment Method"
                        name="paymentTerms.paymentMethod"
                        value={formData.paymentTerms?.paymentMethod || ''}
                        onChange={handleNestedSelectChange}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          '& .MuiSelect-select': {
                            color: 'text.primary',
                          },
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              backgroundColor: 'rgba(30, 30, 30, 0.95)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                            },
                          },
                        }}
                      >
                        <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                        <MenuItem value="DIRECT_DEBIT">Direct Debit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Frequency</InputLabel>
                      <Select
                        label="Payment Frequency"
                        name="paymentTerms.paymentFrequency"
                        value={formData.paymentTerms?.paymentFrequency || ''}
                        onChange={handleNestedSelectChange}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          '& .MuiSelect-select': {
                            color: 'text.primary',
                          },
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              backgroundColor: 'rgba(30, 30, 30, 0.95)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                            },
                          },
                        }}
                      >
                        <MenuItem value="UPFRONT">Upfront</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                        <MenuItem value="ANNUALLY">Annually</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  sx={textFieldSx}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            p: 2,
            gap: 1,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                borderRadius: '0.5rem',
                textTransform: 'none',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            {editingSupplier && (
              <Button 
                onClick={() => handleDelete(editingSupplier)}
                sx={{
                  borderRadius: '0.5rem',
                  textTransform: 'none',
                  backgroundColor: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  }
                }}
                variant="contained"
              >
                Delete
              </Button>
            )}
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={{
                borderRadius: '0.5rem',
                textTransform: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              {editingSupplier ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Suppliers;
